import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import { usePeopleAdmin } from '../context/PeopleAdminContext';
import MetricsCards from '../components/overview/MetricsCards';
import QuickActions from '../components/overview/QuickActions';
import logger from '../../../../../utils/logger';

/**
 * Overview tab component displaying metrics and recent activity
 * @param {Object} props - Component props
 * @param {Function} props.onSwitchToEmployees - Callback to switch to Employees tab
 * @param {Function} props.onSwitchToRoles - Callback to switch to Role Definitions tab
 * @returns {React.Component} Overview tab content
 */
function OverviewTab({ onSwitchToEmployees, onSwitchToRoles }) {
  const { metrics, isLoading, error, refetch } = usePeopleAdmin();
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // Handle loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle retry action
  const handleRetry = async () => {
    logger.info('Retrying data fetch', { 
      component: 'OverviewTab',
      retryCount: retryCount + 1,
      maxRetries 
    });
    
    setRetryCount(prev => prev + 1);
    
    try {
      await refetch();
    } catch (retryError) {
      logger.error('Retry failed', {
        component: 'OverviewTab',
        error: retryError,
        retryCount: retryCount + 1
      });
    }
  };

  // Handle error state with retry mechanism
  if (error) {
    const errorMessage = error?.message || 'Failed to load data';
    const canRetry = retryCount < maxRetries;
    
    logger.error('OverviewTab data loading error', {
      component: 'OverviewTab',
      error: errorMessage,
      retryCount,
      canRetry
    });
    
    return (
      <Box p={3}>
        <Alert 
          severity="error"
          action={
            canRetry && (
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleRetry}
                disabled={isLoading}
              >
                {isLoading ? 'Retrying...' : 'Retry'}
              </Button>
            )
          }
        >
          {errorMessage}
          {canRetry 
            ? ` (Attempt ${retryCount + 1} of ${maxRetries + 1})`
            : ' - Maximum retry attempts reached. Please refresh the page.'
          }
        </Alert>
        {!canRetry && (
          <Box mt={2}>
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Metrics Cards */}
      <Box mb={3}>
        <MetricsCards metrics={metrics} />
      </Box>

      {/* Quick Actions */}
      <Box mb={3}>
        <QuickActions
          onAddEmployee={onSwitchToEmployees}
          onCreateRole={onSwitchToRoles}
          disabled={false}
        />
      </Box>
    </Box>
  );
}

OverviewTab.propTypes = {
  onSwitchToEmployees: PropTypes.func.isRequired,
  onSwitchToRoles: PropTypes.func.isRequired,
};

export default OverviewTab;